import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Img } from 'react-image';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import Button from 'src/components/design-system/Button';
import { DEFAULT_PLACEHOLDERS } from 'src/utils/parseProductImageUrl';

interface EnlargedImageViewProps {
  imageUrl: string;
  title: string;
  height: number;
  onClose: () => void;
}

const minScale = 40;
const maxScale = 500;
const step = 20;
const initialScale = 100;

export default function EnlargedImageView({
  imageUrl,
  title,
  height,
  onClose,
}: EnlargedImageViewProps) {
  const { t } = useTranslation();
  const [scale, setScale] = useState(initialScale);

  const handleZoomIn = useCallback(
    (centerView: (scale?: number) => void) => () => {
      setScale((prev) => {
        const newScale = Number((prev + step).toFixed(2));
        if (newScale <= maxScale) {
          centerView(newScale / 100);
          return newScale;
        }
        centerView(maxScale / 100);
        return maxScale;
      });
    },
    [setScale]
  );

  const handleZoomOut = useCallback(
    (centerView: (scale?: number) => void) => () => {
      setScale((prev) => {
        const newScale = Number((prev - step).toFixed(2));
        if (newScale >= minScale) {
          centerView(newScale / 100);
          return newScale;
        }
        centerView(minScale / 100);
        return minScale;
      });
    },
    [setScale]
  );
  return (
    <div className="bg-gray-100 mt-4">
      {/* Image Container */}
      <div className="flex-1 bg-white flex flex-col">
        <TransformWrapper
          initialScale={scale / 120}
          minScale={minScale / 100}
          maxScale={maxScale / 100}
          centerOnInit
          centerZoomedOut
          disablePadding
          onZoom={(ref) => {
            setScale(Number(ref.state.scale.toFixed(2)) * 100);
          }}
        >
          {({ centerView }) => (
            <>
              {/* Use flex-1 to let the zoom area grow to fill available space */}
              <div className="flex-1">
                <TransformComponent
                  wrapperStyle={{
                    width: '100%',
                    height: `${height - 150}px`,
                    zIndex: 1000,
                  }}
                  contentStyle={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                >
                  <Img
                    src={[imageUrl, DEFAULT_PLACEHOLDERS.noImage]}
                    alt={title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </TransformComponent>
              </div>
              {/* Footer Controls */}
              <div
                className="px-4 pb-3 flex items-center flex-shrink-0"
                style={{ zIndex: 2000, position: 'sticky', bottom: 0 }}
              >
                <div className="flex-grow" />
                <div className="flex items-center gap-2">
                  <span className="text-sm">{t('Zoom')}:</span>
                  <button
                    className="bg-dark rounded-full h-8 w-8 flex items-center justify-center text-white"
                    onClick={handleZoomOut(centerView)}
                  >
                    <span className="k-icon k-i-minus-sm" />
                  </button>
                  <button
                    className="bg-dark rounded-full h-8 w-8 flex items-center justify-center text-white cursor-pointer"
                    onClick={handleZoomIn(centerView)}
                  >
                    <span className="k-icon k-i-plus-sm" />
                  </button>
                </div>
                <div className="flex-grow flex justify-end">
                  <Button color="info" onClick={onClose}>
                    {t('Close')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </TransformWrapper>
      </div>
    </div>
  );
}
